import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Save, Loader, Check, AlertCircle, X, Palette,
  Eye, ExternalLink, Layers, LayoutGrid, List, Info,
  ArrowUp, ArrowDown, Megaphone, Sparkles, Tag, MessageSquare,
  Mail, Plus, Trash2, Package, Search, Store,
  Image as ImageIcon,
  Images, Award, BarChart3, HelpCircle, Columns,
  Percent
} from 'lucide-react';
import './Boutiques.css';
import './Vitrine.css';

const DEFAULT_VITRINE = {
  design: 'classic',
  layout: 'grid',
  primaryColor: '#6366f1',
  heroTitle: '',
  heroSubtitle: '',
  showStock: true,
  showPrice: true,
  showSearch: true,
  produitsMode: 'tous',       // 'tous' | 'selection'
  produitsEnLigne: [],        // ids des produits vendus en ligne (mode « selection »)
  showProductGrid: true,
  productGridTitle: 'Tous nos produits',
  showProductGridTitle: true,
  ctaLabel: 'Ajouter',
  sections: [],
};

const SECTION_TYPES = [
  { type: 'hero',         label: 'Bannière hero',      icon: ImageIcon,     desc: 'Grand visuel principal avec CTA' },
  { type: 'banner',       label: 'Bandeau promo',      icon: Megaphone,     desc: 'Strip coloré, message + icône' },
  { type: 'featured',     label: 'Produits phares',    icon: Sparkles,      desc: 'Vitrine de produits sélectionnés' },
  { type: 'advantages',   label: 'Avantages / Garanties', icon: Award,      desc: '4 icônes : livraison, paiement, etc.' },
  { type: 'stats',        label: 'Statistiques',       icon: BarChart3,     desc: 'Compteurs : 1000+ clients, 4.9★…' },
  { type: 'split',        label: 'Split image+texte',  icon: Columns,       desc: 'Section moitié image, moitié texte' },
  { type: 'gallery',      label: 'Galerie d\'images',  icon: Images,        desc: 'Mosaïque photo (lookbook)' },
  { type: 'categories',   label: 'Catégories',         icon: Tag,           desc: 'Grandes catégories cliquables' },
  { type: 'testimonials', label: 'Témoignages',        icon: MessageSquare, desc: 'Avis clients' },
  { type: 'faq',          label: 'FAQ',                icon: HelpCircle,    desc: 'Questions fréquentes (accordéon)' },
  { type: 'about',        label: 'À propos',           icon: Info,          desc: 'Bloc de présentation' },
  { type: 'newsletter',   label: 'Inscription email',  icon: Mail,          desc: 'Capture d\'email' },
];

const makeSection = (type) => {
  const base = { id: `s-${Date.now()}-${Math.floor(Math.random() * 1000)}`, type, enabled: true };
  switch (type) {
    case 'hero':
      return { ...base, config: {
        tag: 'Nouveau',
        title: 'Bienvenue dans notre boutique',
        subtitle: 'Découvrez nos produits, paiement à la livraison',
        ctaLabel: 'Découvrir',
        align: 'center',
        height: 'medium',
        imageUrl: ''
      } };
    case 'banner':
      return { ...base, config: { text: 'Livraison gratuite à Dakar dès 10 000 XOF', icon: '🚚', bgColor: '#fff8ec', textColor: '#92400e' } };
    case 'featured':
      return { ...base, config: { title: 'Nos produits phares', subtitle: 'Sélectionnés avec soin pour vous', produitIds: [] } };
    case 'categories':
      return { ...base, config: { title: 'Parcourir par catégorie', items: [{ label: 'Boissons' }, { label: 'Alimentaire' }, { label: 'Hygiène' }] } };
    case 'testimonials':
      return { ...base, config: { title: 'Ils nous font confiance', items: [{ author: 'Aïssatou D.', role: 'Cliente fidèle', text: 'Livraison rapide et produits de qualité.', rating: 5 }] } };
    case 'about':
      return { ...base, config: { title: 'À propos de nous', text: 'Décrivez votre boutique, votre mission, vos engagements.' } };
    case 'newsletter':
      return { ...base, config: { title: 'Restez informé', subtitle: 'Recevez nos promos et nouveautés', placeholder: 'votre@email.com', ctaLabel: 'Je m\'inscris' } };
    case 'advantages':
      return { ...base, config: {
        title: 'Pourquoi nous choisir',
        items: [
          { icon: '🚚', title: 'Livraison rapide', text: 'Livraison sous 24 à 48h' },
          { icon: '💳', title: 'Paiement à la livraison', text: 'Aucun paiement en ligne' },
          { icon: '✅', title: 'Qualité garantie', text: 'Produits sélectionnés avec soin' },
          { icon: '☎️', title: 'Support 7j/7',  text: 'Notre équipe à votre écoute' },
        ]
      } };
    case 'stats':
      return { ...base, config: {
        title: 'Notre boutique en chiffres',
        items: [
          { value: '1000+', label: 'Clients satisfaits' },
          { value: '500+',  label: 'Produits référencés' },
          { value: '4.9',   label: 'Note moyenne ★' },
          { value: '24h',   label: 'Délai de livraison' },
        ]
      } };
    case 'split':
      return { ...base, config: {
        title: 'Notre histoire',
        subtitle: 'Une boutique fondée par la passion',
        text: 'Nous mettons un soin particulier à sélectionner nos produits pour offrir le meilleur à nos clients. Découvrez notre univers.',
        ctaLabel: 'En savoir plus',
        imageUrl: '',
        imagePosition: 'right',
      } };
    case 'gallery':
      return { ...base, config: {
        title: 'Notre univers',
        subtitle: 'Découvrez nos produits en images',
        images: ['', '', '', '', '', ''],
      } };
    case 'faq':
      return { ...base, config: {
        title: 'Questions fréquentes',
        items: [
          { question: 'Comment puis-je commander ?', answer: 'Ajoutez vos produits au panier, remplissez vos coordonnées, et validez la commande. Vous paierez à la livraison.' },
          { question: 'Quels sont les délais de livraison ?', answer: 'Nous livrons sous 24 à 48h dans la zone couverte.' },
          { question: 'Puis-je annuler ma commande ?', answer: 'Oui, contactez-nous avant que la commande soit expédiée.' },
        ]
      } };
    default:
      return { ...base, config: {} };
  }
};

export default function Vitrine() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [boutique, setBoutique] = useState(null);
  const [cfg, setCfg] = useState(DEFAULT_VITRINE);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Product picker (pour sections featured)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSectionId, setPickerSectionId] = useState(null);
  const [pickerProducts, setPickerProducts] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSelected, setPickerSelected] = useState([]);

  // Produits vendus en ligne (mode « sélection »)
  const [rechercheEnLigne, setRechercheEnLigne] = useState('');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [bRes, pRes] = await Promise.all([
        axios.get('/api/boutiques'),
        axios.get('/api/produits', { params: { boutiqueId: id } }).catch(() => ({ data: { data: [] } })),
      ]);
      const list = Array.isArray(bRes.data) ? bRes.data : (bRes.data.data || []);
      const b = list.find(x => String(x._id) === String(id));
      if (!b) {
        showToast('error', 'Boutique introuvable');
        navigate('/boutiques');
        return;
      }
      setBoutique(b);
      setCfg({
        ...DEFAULT_VITRINE, ...(b.vitrineConfig || {}),
        promotions: (b.vitrineConfig?.promotions || []),
        produitsEnLigne: (b.vitrineConfig?.produitsEnLigne || []).map(x => String(x?._id || x)),
      });
      setAllProducts(pRes.data.data || []);
    } catch (e) {
      showToast('error', 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const shopUrl = boutique ? `${window.location.origin}/shop/${boutique._id}` : '';

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/boutiques/${id}/vitrine`, cfg);
      showToast('success', 'Vitrine enregistrée');
    } catch (e) {
      showToast('error', e.response?.data?.message || 'Erreur d\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const copyShopLink = async () => {
    try {
      await navigator.clipboard.writeText(shopUrl);
      showToast('success', 'Lien copié');
    } catch {
      showToast('error', 'Impossible de copier');
    }
  };

  // Sections helpers
  // ── Produits vendus en ligne ──
  const enLigneSet = new Set(cfg.produitsEnLigne || []);
  const produitsEnLigneFiltres = (() => {
    const q = rechercheEnLigne.toLowerCase().trim();
    if (!q) return allProducts;
    return allProducts.filter(p =>
      (p.nom || '').toLowerCase().includes(q) ||
      (p.codeProduit || '').toLowerCase().includes(q) ||
      (p.codeBarres || '').toLowerCase().includes(q));
  })();
  const toggleEnLigne = (pid) => setCfg(c => {
    const cur = new Set(c.produitsEnLigne || []);
    cur.has(pid) ? cur.delete(pid) : cur.add(pid);
    return { ...c, produitsEnLigne: [...cur] };
  });
  // Coche / décoche tous les produits affichés (respecte la recherche en cours)
  const toutCocherEnLigne = (cocher) => setCfg(c => {
    const cur = new Set(c.produitsEnLigne || []);
    for (const p of produitsEnLigneFiltres) cocher ? cur.add(String(p._id)) : cur.delete(String(p._id));
    return { ...c, produitsEnLigne: [...cur] };
  });

  const addSection = (type) => setCfg(c => ({ ...c, sections: [...(c.sections || []), makeSection(type)] }));
  const removeSection = (sid) => setCfg(c => ({ ...c, sections: (c.sections || []).filter(s => s.id !== sid) }));
  const moveSection = (sid, dir) => setCfg(c => {
    const arr = [...(c.sections || [])];
    const idx = arr.findIndex(s => s.id === sid);
    const t = idx + dir;
    if (idx < 0 || t < 0 || t >= arr.length) return c;
    [arr[idx], arr[t]] = [arr[t], arr[idx]];
    return { ...c, sections: arr };
  });
  const updateSection = (sid, patch) => setCfg(c => ({
    ...c,
    sections: (c.sections || []).map(s => s.id === sid ? { ...s, config: { ...(s.config || {}), ...patch } } : s)
  }));
  const toggleSection = (sid) => setCfg(c => ({
    ...c,
    sections: (c.sections || []).map(s => s.id === sid ? { ...s, enabled: !s.enabled } : s)
  }));

  // ── Promotions ──
  const addPromotion = () => {
    const promo = {
      id: `p-${Date.now()}-${Math.floor(Math.random()*1000)}`,
      name: 'Nouvelle promo',
      type: 'percent',
      value: 10,
      targetType: 'all',
      targetIds: [],
      active: true,
    };
    setCfg(c => ({ ...c, promotions: [...(c.promotions || []), promo] }));
  };
  const updatePromotion = (pid, patch) => {
    setCfg(c => ({ ...c, promotions: (c.promotions || []).map(p => p.id === pid ? { ...p, ...patch } : p) }));
  };
  const removePromotion = (pid) => {
    setCfg(c => ({ ...c, promotions: (c.promotions || []).filter(p => p.id !== pid) }));
  };

  // Picker
  const openPicker = async (sectionId) => {
    const section = (cfg.sections || []).find(s => s.id === sectionId);
    setPickerSectionId(sectionId);
    setPickerSelected(section?.config?.produitIds || []);
    setPickerSearch('');
    setPickerOpen(true);
    setPickerLoading(true);
    try {
      const { data } = await axios.get('/api/produits', { params: { boutiqueId: id } });
      setPickerProducts(data.data || []);
    } catch {
      showToast('error', 'Erreur de chargement des produits');
    } finally {
      setPickerLoading(false);
    }
  };
  const togglePickerProduct = (pid) => setPickerSelected(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  const confirmPicker = () => {
    setCfg(c => ({
      ...c,
      sections: (c.sections || []).map(s =>
        s.id === pickerSectionId ? { ...s, config: { ...s.config, produitIds: pickerSelected } } : s
      )
    }));
    setPickerOpen(false);
  };

  const pickerFiltered = useMemo(() => {
    if (!pickerSearch) return pickerProducts;
    const q = pickerSearch.toLowerCase();
    return pickerProducts.filter(p =>
      (p.nom || '').toLowerCase().includes(q) ||
      (p.codeProduit || '').toLowerCase().includes(q));
  }, [pickerProducts, pickerSearch]);

  if (loading) return (
    <div className="bt-loading"><Loader className="bt-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="bt-page vt-page">
      {toast && (
        <div className={`bt-toast bt-toast--${toast.type}`}>
          {toast.type === 'success' ? <Check size={14}/> : <AlertCircle size={14}/>}
          {toast.text}
        </div>
      )}

      {/* Header — même style que la page Boutiques */}
      <div className="bt-header">
        <div className="bt-header-left">
          <button className="vt-back" onClick={() => navigate('/boutiques')}>
            <ArrowLeft size={16}/>
          </button>
          <div className="bt-header-icon"><Palette size={20}/></div>
          <div>
            <h1 className="bt-header-title">Personnaliser la vitrine</h1>
            <p className="bt-header-sub">
              <Store size={11}/> {boutique?.nom} · Composez votre vitrine publique
            </p>
          </div>
        </div>
        <div className="vt-header-actions">
          <button className="vt-secondary-btn" onClick={copyShopLink}>
            <ExternalLink size={13}/> Copier le lien
          </button>
          <a className="vt-secondary-btn" href={shopUrl} target="_blank" rel="noreferrer">
            <Eye size={13}/> Voir la vitrine
          </a>
          <button className="bt-cta-btn" onClick={save} disabled={saving}>
            {saving ? <><Loader size={13} className="bt-spin"/> Enregistrement…</> : <><Save size={13}/> Enregistrer</>}
          </button>
        </div>
      </div>

      <div className="vt-content">
        {/* Colonne configuration */}
        <div className="vt-config">

          {/* ════ SECTIONS de la vitrine — au TOP ════ */}
          <div className="vt-panel vt-panel--highlight">
            <div className="vt-panel-head">
              <Layers size={14}/>
              <div>
                <h3>Sections de la vitrine</h3>
                <p>Ajoutez des blocs qui s'affichent avant la grille principale. Cliquez sur un type pour l'ajouter.</p>
              </div>
            </div>

            <div className="vt-section-types">
              {SECTION_TYPES.map(t => {
                const I = t.icon;
                return (
                  <button key={t.type} type="button" className="vt-section-type" onClick={() => addSection(t.type)}>
                    <I size={16}/>
                    <strong>{t.label}</strong>
                    <span>{t.desc}</span>
                  </button>
                );
              })}
            </div>

            {(cfg.sections || []).length === 0 ? (
              <div className="vt-empty">Aucune section pour le moment — cliquez sur un type ci-dessus.</div>
            ) : (
              <div className="vt-sections-list">
                {cfg.sections.map((s, idx) => {
                  const info = SECTION_TYPES.find(t => t.type === s.type) || { label: s.type, icon: Info };
                  const I = info.icon;
                  return (
                    <div key={s.id} className={`vt-section ${!s.enabled ? 'vt-section--off' : ''}`}>
                      <div className="vt-section-head">
                        <div className="vt-section-title"><I size={13}/> {info.label}</div>
                        <div className="vt-section-actions">
                          <button type="button"
                            className={`vt-visibility-toggle ${s.enabled ? 'vt-visibility-toggle--on' : 'vt-visibility-toggle--off'}`}
                            onClick={() => toggleSection(s.id)}>
                            {s.enabled ? <><Eye size={11}/> Affichée</> : <><X size={11}/> Masquée</>}
                          </button>
                          <button type="button" disabled={idx === 0} onClick={() => moveSection(s.id, -1)} title="Monter"><ArrowUp size={12}/></button>
                          <button type="button" disabled={idx === cfg.sections.length - 1} onClick={() => moveSection(s.id, 1)} title="Descendre"><ArrowDown size={12}/></button>
                          <button type="button" className="vt-danger" onClick={() => removeSection(s.id)} title="Supprimer"><Trash2 size={12}/></button>
                        </div>
                      </div>

                      <div className="vt-section-body">
                        {s.type === 'hero' && (
                          <>
                            <input type="text" className="vt-input" placeholder="Étiquette (Nouveau, -20%, etc.)"
                              value={s.config?.tag || ''}
                              onChange={e => updateSection(s.id, { tag: e.target.value })}/>
                            <input type="text" className="vt-input" placeholder="Titre principal"
                              value={s.config?.title || ''}
                              onChange={e => updateSection(s.id, { title: e.target.value })}/>
                            <input type="text" className="vt-input" placeholder="Sous-titre"
                              value={s.config?.subtitle || ''}
                              onChange={e => updateSection(s.id, { subtitle: e.target.value })}/>
                            <input type="text" className="vt-input" placeholder="Texte du bouton CTA (vide = pas de bouton)"
                              value={s.config?.ctaLabel || ''}
                              onChange={e => updateSection(s.id, { ctaLabel: e.target.value })}/>
                            <input type="url" className="vt-input" placeholder="URL de l'image de fond (vide = dégradé couleur)"
                              value={s.config?.imageUrl || ''}
                              onChange={e => updateSection(s.id, { imageUrl: e.target.value })}/>
                            <div className="vt-row">
                              <select className="vt-input" value={s.config?.align || 'center'}
                                onChange={e => updateSection(s.id, { align: e.target.value })}>
                                <option value="left">Aligné à gauche</option>
                                <option value="center">Centré</option>
                                <option value="right">Aligné à droite</option>
                              </select>
                              <select className="vt-input" value={s.config?.height || 'medium'}
                                onChange={e => updateSection(s.id, { height: e.target.value })}>
                                <option value="small">Petit (220 px)</option>
                                <option value="medium">Moyen (320 px)</option>
                                <option value="large">Grand (420 px)</option>
                              </select>
                            </div>
                          </>
                        )}

                        {s.type === 'banner' && (
                          <>
                            <input type="text" className="vt-input" placeholder="Titre (optionnel)"
                              value={s.config?.title || ''}
                              onChange={e => updateSection(s.id, { title: e.target.value })}/>
                            <input type="text" className="vt-input" placeholder="Texte de la bannière"
                              value={s.config?.text || ''}
                              onChange={e => updateSection(s.id, { text: e.target.value })}/>
                            <div className="vt-row">
                              <input type="text" className="vt-input" style={{ width: 70 }} placeholder="🚚"
                                value={s.config?.icon || ''}
                                onChange={e => updateSection(s.id, { icon: e.target.value })}/>
                              <input type="color" className="vt-color"
                                value={s.config?.bgColor || '#fff8ec'}
                                onChange={e => updateSection(s.id, { bgColor: e.target.value })} title="Fond"/>
                              <input type="color" className="vt-color"
                                value={s.config?.textColor || '#92400e'}
                                onChange={e => updateSection(s.id, { textColor: e.target.value })} title="Texte"/>
                            </div>
                          </>
                        )}

                        {s.type === 'featured' && (
                          <>
                            <input type="text" className="vt-input" placeholder="Titre"
                              value={s.config?.title || ''}
                              onChange={e => updateSection(s.id, { title: e.target.value })}/>
                            <input type="text" className="vt-input" placeholder="Sous-titre"
                              value={s.config?.subtitle || ''}
                              onChange={e => updateSection(s.id, { subtitle: e.target.value })}/>
                            <button type="button" className="vt-pick-btn" onClick={() => openPicker(s.id)}>
                              <Package size={13}/>
                              {(s.config?.produitIds || []).length > 0
                                ? `${(s.config.produitIds).length} produit(s) sélectionné(s) — modifier`
                                : 'Choisir les produits phares'}
                            </button>
                          </>
                        )}

                        {s.type === 'categories' && (
                          <>
                            <input type="text" className="vt-input" placeholder="Titre"
                              value={s.config?.title || ''}
                              onChange={e => updateSection(s.id, { title: e.target.value })}/>
                            <div className="vt-chips">
                              {(s.config?.items || []).map((it, i) => (
                                <span key={i} className="vt-chip">
                                  <input type="text" value={it.label}
                                    onChange={e => {
                                      const items = [...s.config.items];
                                      items[i] = { ...items[i], label: e.target.value };
                                      updateSection(s.id, { items });
                                    }}/>
                                  <button type="button" onClick={() => {
                                    const items = s.config.items.filter((_, k) => k !== i);
                                    updateSection(s.id, { items });
                                  }}><X size={10}/></button>
                                </span>
                              ))}
                              <button type="button" className="vt-chip-add"
                                onClick={() => updateSection(s.id, { items: [...(s.config?.items || []), { label: 'Nouvelle' }] })}>
                                <Plus size={11}/> chip
                              </button>
                            </div>
                          </>
                        )}

                        {s.type === 'testimonials' && (
                          <>
                            <input type="text" className="vt-input" placeholder="Titre"
                              value={s.config?.title || ''}
                              onChange={e => updateSection(s.id, { title: e.target.value })}/>
                            {(s.config?.items || []).map((it, i) => (
                              <div key={i} className="vt-testi-edit">
                                <div className="vt-row">
                                  <input type="text" placeholder="Nom" value={it.author || ''}
                                    onChange={e => {
                                      const items = [...s.config.items];
                                      items[i] = { ...items[i], author: e.target.value };
                                      updateSection(s.id, { items });
                                    }}/>
                                  <input type="number" min="1" max="5" placeholder="★" value={it.rating || 5} style={{ width: 60 }}
                                    onChange={e => {
                                      const items = [...s.config.items];
                                      items[i] = { ...items[i], rating: parseInt(e.target.value) || 5 };
                                      updateSection(s.id, { items });
                                    }}/>
                                  <button type="button" className="vt-x-btn" onClick={() => {
                                    const items = s.config.items.filter((_, k) => k !== i);
                                    updateSection(s.id, { items });
                                  }}><X size={11}/></button>
                                </div>
                                <textarea placeholder="Témoignage" rows="2" value={it.text || ''}
                                  onChange={e => {
                                    const items = [...s.config.items];
                                    items[i] = { ...items[i], text: e.target.value };
                                    updateSection(s.id, { items });
                                  }}/>
                              </div>
                            ))}
                            <button type="button" className="vt-chip-add"
                              onClick={() => updateSection(s.id, {
                                items: [...(s.config?.items || []), { author: '', text: '', rating: 5 }]
                              })}>
                              <Plus size={11}/> témoignage
                            </button>
                          </>
                        )}

                        {s.type === 'advantages' && (
                          <>
                            <input type="text" className="vt-input" placeholder="Titre de la section"
                              value={s.config?.title || ''}
                              onChange={e => updateSection(s.id, { title: e.target.value })}/>
                            {(s.config?.items || []).map((it, i) => (
                              <div key={i} className="vt-mini-editor">
                                <div className="vt-row">
                                  <input type="text" placeholder="🚚" style={{ width: 50 }}
                                    value={it.icon || ''}
                                    onChange={e => {
                                      const items = [...s.config.items];
                                      items[i] = { ...items[i], icon: e.target.value };
                                      updateSection(s.id, { items });
                                    }}/>
                                  <input type="text" placeholder="Titre" value={it.title || ''}
                                    onChange={e => {
                                      const items = [...s.config.items];
                                      items[i] = { ...items[i], title: e.target.value };
                                      updateSection(s.id, { items });
                                    }}/>
                                  <button type="button" className="vt-x-btn" onClick={() => {
                                    const items = s.config.items.filter((_, k) => k !== i);
                                    updateSection(s.id, { items });
                                  }}><X size={11}/></button>
                                </div>
                                <input type="text" className="vt-input" placeholder="Description"
                                  value={it.text || ''}
                                  onChange={e => {
                                    const items = [...s.config.items];
                                    items[i] = { ...items[i], text: e.target.value };
                                    updateSection(s.id, { items });
                                  }}
                                  style={{ marginTop: 4 }}/>
                              </div>
                            ))}
                            <button type="button" className="vt-chip-add"
                              onClick={() => updateSection(s.id, { items: [...(s.config?.items || []), { icon: '✨', title: '', text: '' }] })}>
                              <Plus size={11}/> avantage
                            </button>
                          </>
                        )}

                        {s.type === 'stats' && (
                          <>
                            <input type="text" className="vt-input" placeholder="Titre"
                              value={s.config?.title || ''}
                              onChange={e => updateSection(s.id, { title: e.target.value })}/>
                            {(s.config?.items || []).map((it, i) => (
                              <div key={i} className="vt-row">
                                <input type="text" placeholder="1000+" style={{ width: 100 }} value={it.value || ''}
                                  onChange={e => {
                                    const items = [...s.config.items];
                                    items[i] = { ...items[i], value: e.target.value };
                                    updateSection(s.id, { items });
                                  }}/>
                                <input type="text" placeholder="Libellé" value={it.label || ''}
                                  onChange={e => {
                                    const items = [...s.config.items];
                                    items[i] = { ...items[i], label: e.target.value };
                                    updateSection(s.id, { items });
                                  }}/>
                                <button type="button" className="vt-x-btn" onClick={() => {
                                  const items = s.config.items.filter((_, k) => k !== i);
                                  updateSection(s.id, { items });
                                }}><X size={11}/></button>
                              </div>
                            ))}
                            <button type="button" className="vt-chip-add"
                              onClick={() => updateSection(s.id, { items: [...(s.config?.items || []), { value: '', label: '' }] })}>
                              <Plus size={11}/> stat
                            </button>
                          </>
                        )}

                        {s.type === 'split' && (
                          <>
                            <input type="text" className="vt-input" placeholder="Titre"
                              value={s.config?.title || ''}
                              onChange={e => updateSection(s.id, { title: e.target.value })}/>
                            <input type="text" className="vt-input" placeholder="Sous-titre"
                              value={s.config?.subtitle || ''}
                              onChange={e => updateSection(s.id, { subtitle: e.target.value })}/>
                            <textarea className="vt-input" rows="3" placeholder="Texte"
                              value={s.config?.text || ''}
                              onChange={e => updateSection(s.id, { text: e.target.value })}/>
                            <input type="text" className="vt-input" placeholder="Libellé bouton (vide = pas de bouton)"
                              value={s.config?.ctaLabel || ''}
                              onChange={e => updateSection(s.id, { ctaLabel: e.target.value })}/>
                            <input type="url" className="vt-input" placeholder="URL de l'image"
                              value={s.config?.imageUrl || ''}
                              onChange={e => updateSection(s.id, { imageUrl: e.target.value })}/>
                            <select className="vt-input" value={s.config?.imagePosition || 'right'}
                              onChange={e => updateSection(s.id, { imagePosition: e.target.value })}>
                              <option value="left">Image à gauche</option>
                              <option value="right">Image à droite</option>
                            </select>

                            <div className="vt-field-label" style={{ marginTop: 8 }}>Le bouton renvoie vers :</div>
                            <select className="vt-input"
                              value={s.config?.linkType || 'none'}
                              onChange={e => updateSection(s.id, { linkType: e.target.value, linkId: '' })}>
                              <option value="none">Aucun (scroll vers la grille)</option>
                              <option value="product">Un produit précis (scroll + surbrillance)</option>
                            </select>

                            {s.config?.linkType === 'product' && (
                              <select className="vt-input"
                                value={s.config?.linkId || ''}
                                onChange={e => updateSection(s.id, { linkId: e.target.value })}>
                                <option value="">— Choisir un produit —</option>
                                {allProducts.map(p => (
                                  <option key={p._id} value={p._id}>
                                    {p.nom} ({p.codeProduit})
                                  </option>
                                ))}
                              </select>
                            )}
                          </>
                        )}

                        {s.type === 'gallery' && (
                          <>
                            <input type="text" className="vt-input" placeholder="Titre"
                              value={s.config?.title || ''}
                              onChange={e => updateSection(s.id, { title: e.target.value })}/>
                            <input type="text" className="vt-input" placeholder="Sous-titre"
                              value={s.config?.subtitle || ''}
                              onChange={e => updateSection(s.id, { subtitle: e.target.value })}/>
                            <div className="vt-gallery-editor">
                              {(s.config?.images || []).map((url, i) => (
                                <div key={i} className="vt-row">
                                  <input type="url" placeholder={`URL image ${i + 1}`}
                                    value={url}
                                    onChange={e => {
                                      const images = [...s.config.images];
                                      images[i] = e.target.value;
                                      updateSection(s.id, { images });
                                    }}/>
                                  <button type="button" className="vt-x-btn" onClick={() => {
                                    const images = s.config.images.filter((_, k) => k !== i);
                                    updateSection(s.id, { images });
                                  }}><X size={11}/></button>
                                </div>
                              ))}
                              <button type="button" className="vt-chip-add"
                                onClick={() => updateSection(s.id, { images: [...(s.config?.images || []), ''] })}>
                                <Plus size={11}/> image
                              </button>
                            </div>
                          </>
                        )}

                        {s.type === 'faq' && (
                          <>
                            <input type="text" className="vt-input" placeholder="Titre"
                              value={s.config?.title || ''}
                              onChange={e => updateSection(s.id, { title: e.target.value })}/>
                            {(s.config?.items || []).map((it, i) => (
                              <div key={i} className="vt-mini-editor">
                                <div className="vt-row">
                                  <input type="text" placeholder="Question" value={it.question || ''}
                                    onChange={e => {
                                      const items = [...s.config.items];
                                      items[i] = { ...items[i], question: e.target.value };
                                      updateSection(s.id, { items });
                                    }}/>
                                  <button type="button" className="vt-x-btn" onClick={() => {
                                    const items = s.config.items.filter((_, k) => k !== i);
                                    updateSection(s.id, { items });
                                  }}><X size={11}/></button>
                                </div>
                                <textarea placeholder="Réponse" rows="2" value={it.answer || ''}
                                  onChange={e => {
                                    const items = [...s.config.items];
                                    items[i] = { ...items[i], answer: e.target.value };
                                    updateSection(s.id, { items });
                                  }}/>
                              </div>
                            ))}
                            <button type="button" className="vt-chip-add"
                              onClick={() => updateSection(s.id, { items: [...(s.config?.items || []), { question: '', answer: '' }] })}>
                              <Plus size={11}/> question
                            </button>
                          </>
                        )}

                        {s.type === 'about' && (
                          <>
                            <input type="text" className="vt-input" placeholder="Titre"
                              value={s.config?.title || ''}
                              onChange={e => updateSection(s.id, { title: e.target.value })}/>
                            <textarea className="vt-input" rows="3" placeholder="Texte de présentation"
                              value={s.config?.text || ''}
                              onChange={e => updateSection(s.id, { text: e.target.value })}/>
                          </>
                        )}

                        {s.type === 'newsletter' && (
                          <>
                            <input type="text" className="vt-input" placeholder="Titre"
                              value={s.config?.title || ''}
                              onChange={e => updateSection(s.id, { title: e.target.value })}/>
                            <input type="text" className="vt-input" placeholder="Sous-titre"
                              value={s.config?.subtitle || ''}
                              onChange={e => updateSection(s.id, { subtitle: e.target.value })}/>
                            <input type="text" className="vt-input" placeholder="Libellé bouton"
                              value={s.config?.ctaLabel || ''}
                              onChange={e => updateSection(s.id, { ctaLabel: e.target.value })}/>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ════ Design ════ */}
          <div className="vt-panel">
            <div className="vt-panel-head">
              <Layers size={14}/>
              <div><h3>Design des produits</h3><p>Style visuel des cartes</p></div>
            </div>
            <div className="vt-opts vt-opts-2">
              {[
                { v: 'classic', l: 'Classique', d: 'Carte standard, équilibrée' },
                { v: 'minimal', l: 'Minimal',   d: 'Sans bordure, image en avant' },
                { v: 'modern',  l: 'Modern',    d: 'Overlay gradient + badge' },
                { v: 'luxury',  l: 'Luxury',    d: 'Beige + or, typographie serif' },
                { v: 'premium', l: 'Premium',   d: 'Fond sombre, accents dorés' },
                { v: 'compact', l: 'Compact',   d: 'Plus dense, image à gauche' },
              ].map(o => (
                <button key={o.v} type="button"
                  className={`vt-opt ${cfg.design === o.v ? 'vt-opt--active' : ''}`}
                  onClick={() => setCfg(c => ({ ...c, design: o.v }))}>
                  <span className={`vt-thumb vt-thumb--${o.v}`}/>
                  <strong>{o.l}</strong>
                  <span>{o.d}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ════ Disposition ════ */}
          <div className="vt-panel">
            <div className="vt-panel-head">
              <LayoutGrid size={14}/>
              <div><h3>Disposition</h3><p>Arrangement des produits</p></div>
            </div>
            <div className="vt-opts vt-opts-3">
              {[
                { v: 'grid',   l: 'Grille',   icon: LayoutGrid, d: '3-4 colonnes responsive' },
                { v: 'list',   l: 'Liste',    icon: List,       d: 'Une ligne par produit' },
                { v: 'mosaic', l: 'Mosaïque', icon: Layers,     d: 'Cartes plus larges' },
              ].map(o => {
                const I = o.icon;
                return (
                  <button key={o.v} type="button"
                    className={`vt-opt ${cfg.layout === o.v ? 'vt-opt--active' : ''}`}
                    onClick={() => setCfg(c => ({ ...c, layout: o.v }))}>
                    <span className="vt-layout-ico"><I size={20}/></span>
                    <strong>{o.l}</strong>
                    <span>{o.d}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ════ Couleur ════ */}
          <div className="vt-panel">
            <div className="vt-panel-head">
              <Palette size={14}/>
              <div><h3>Couleur d'accent</h3><p>Boutons, hero, accents</p></div>
            </div>
            <div className="vt-colors">
              {['#6366f1','#fca311','#10b981','#ef4444','#0ea5e9','#8b5cf6','#ec4899','#0f172a'].map(c => (
                <button key={c} type="button"
                  className={`vt-color-pick ${cfg.primaryColor === c ? 'vt-color-pick--active' : ''}`}
                  style={{ background: c }}
                  onClick={() => setCfg(cf => ({ ...cf, primaryColor: c }))}/>
              ))}
              <input type="color" className="vt-color"
                value={cfg.primaryColor}
                onChange={e => setCfg(c => ({ ...c, primaryColor: e.target.value }))}/>
            </div>
          </div>

          {/* ════ Produits vendus en ligne ════ */}
          <div className="vt-panel">
            <div className="vt-panel-head">
              <Store size={14}/>
              <div><h3>Produits vendus en ligne</h3><p>Choisissez les produits proposés sur la vitrine</p></div>
            </div>
            <label className="vt-check">
              <input type="radio" name="produitsMode" checked={cfg.produitsMode !== 'selection'}
                onChange={() => setCfg(c => ({ ...c, produitsMode: 'tous' }))}/>
              Tous les produits actifs en stock
            </label>
            <label className="vt-check">
              <input type="radio" name="produitsMode" checked={cfg.produitsMode === 'selection'}
                onChange={() => setCfg(c => ({ ...c, produitsMode: 'selection' }))}/>
              Seulement les produits sélectionnés ({enLigneSet.size})
            </label>
            {cfg.produitsMode === 'selection' && (
              <div className="vt-enligne">
                <div className="vt-enligne-bar">
                  <Search size={13}/>
                  <input type="text" placeholder="Rechercher un produit…" value={rechercheEnLigne}
                    onChange={e => setRechercheEnLigne(e.target.value)}/>
                  <button type="button" onClick={() => toutCocherEnLigne(true)}>Tout cocher</button>
                  <button type="button" onClick={() => toutCocherEnLigne(false)}>Tout décocher</button>
                </div>
                <div className="vt-enligne-list">
                  {produitsEnLigneFiltres.length === 0 ? (
                    <div className="vt-enligne-empty">Aucun produit</div>
                  ) : produitsEnLigneFiltres.map(p => {
                    const pid = String(p._id);
                    const stock = Number(p.quantiteStock) || 0;
                    return (
                      <label key={pid} className={`vt-enligne-item${enLigneSet.has(pid) ? ' vt-enligne-item--on' : ''}`}>
                        <input type="checkbox" checked={enLigneSet.has(pid)} onChange={() => toggleEnLigne(pid)}/>
                        <span className="vt-enligne-nom">{p.nom}</span>
                        <span className="vt-enligne-meta">{p.codeProduit}</span>
                        <span className={`vt-enligne-stock${stock <= 0 ? ' vt-enligne-stock--out' : ''}`}>
                          {stock <= 0 ? 'Rupture' : `Stock ${stock}`}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <p className="vt-enligne-hint">
                  Les produits en rupture restent masqués sur la vitrine même s'ils sont cochés.
                </p>
              </div>
            )}
          </div>

          {/* ════ Grille principale (catalogue) ════ */}
          <div className="vt-panel">
            <div className="vt-panel-head">
              <Package size={14}/>
              <div><h3>Grille principale (catalogue)</h3><p>Tous les produits affichés en grille</p></div>
            </div>
            <label className="vt-check">
              <input type="checkbox" checked={cfg.showProductGrid !== false}
                onChange={e => setCfg(c => ({ ...c, showProductGrid: e.target.checked }))}/>
              Afficher la grille principale de tous les produits
            </label>
            <label className="vt-check">
              <input type="checkbox" checked={cfg.showProductGridTitle !== false}
                onChange={e => setCfg(c => ({ ...c, showProductGridTitle: e.target.checked }))}/>
              Afficher un titre au-dessus de la grille
            </label>
            <input type="text" className="vt-input"
              placeholder="Titre de la grille (Tous nos produits, Catalogue, …)"
              value={cfg.productGridTitle || ''}
              onChange={e => setCfg(c => ({ ...c, productGridTitle: e.target.value }))}/>
            <label className="vt-check">
              <input type="checkbox" checked={cfg.showSearch}
                onChange={e => setCfg(c => ({ ...c, showSearch: e.target.checked }))}/>
              Afficher la barre de recherche
            </label>
            <label className="vt-check">
              <input type="checkbox" checked={cfg.showStock}
                onChange={e => setCfg(c => ({ ...c, showStock: e.target.checked }))}/>
              Afficher l'indicateur de stock sur les cartes
            </label>
            <label className="vt-check">
              <input type="checkbox" checked={cfg.showPrice !== false}
                onChange={e => setCfg(c => ({ ...c, showPrice: e.target.checked }))}/>
              Afficher les prix (sinon « Prix sur demande », prix communiqué à la confirmation)
            </label>
            <input type="text" className="vt-input" placeholder="Libellé du bouton (Ajouter)"
              value={cfg.ctaLabel}
              onChange={e => setCfg(c => ({ ...c, ctaLabel: e.target.value }))}/>
          </div>

          {/* ════ Promotions ════ */}
          <div className="vt-panel vt-panel--promo">
            <div className="vt-panel-head">
              <Percent size={14}/>
              <div>
                <h3>Promotions</h3>
                <p>Réductions appliquées à tous les produits ou à des produits spécifiques.</p>
              </div>
            </div>

            <button type="button" className="vt-chip-add" onClick={addPromotion} style={{ marginBottom: 10 }}>
              <Plus size={11}/> Nouvelle promotion
            </button>

            {(cfg.promotions || []).length === 0 ? (
              <div className="vt-empty">Aucune promotion. Cliquez sur le bouton ci-dessus pour en créer une.</div>
            ) : (
              <div className="vt-promo-list">
                {(cfg.promotions || []).map(p => (
                  <div key={p.id} className={`vt-promo-card ${!p.active ? 'vt-promo-card--off' : ''}`}>
                    <div className="vt-promo-head">
                      <input type="text" className="vt-input vt-promo-name" placeholder="Nom de la promo"
                        value={p.name || ''}
                        onChange={e => updatePromotion(p.id, { name: e.target.value })}/>
                      <div className="vt-section-actions">
                        <button type="button"
                          className={`vt-visibility-toggle ${p.active ? 'vt-visibility-toggle--on' : 'vt-visibility-toggle--off'}`}
                          onClick={() => updatePromotion(p.id, { active: !p.active })}>
                          {p.active ? <><Eye size={11}/> Active</> : <><X size={11}/> Inactive</>}
                        </button>
                        <button type="button" className="vt-danger" onClick={() => removePromotion(p.id)}><Trash2 size={11}/></button>
                      </div>
                    </div>

                    <div className="vt-promo-value-row">
                      <input type="number" min="0" className="vt-input vt-promo-value"
                        value={p.value || 0}
                        onChange={e => updatePromotion(p.id, { value: parseFloat(e.target.value) || 0 })}/>
                      <select className="vt-input vt-promo-type"
                        value={p.type || 'percent'}
                        onChange={e => updatePromotion(p.id, { type: e.target.value })}>
                        <option value="percent">% (pourcentage)</option>
                        <option value="fixed">XOF (montant fixe)</option>
                      </select>
                    </div>

                    <div className="vt-field-label">Appliquer à :</div>
                    <select className="vt-input"
                      value={p.targetType || 'all'}
                      onChange={e => updatePromotion(p.id, { targetType: e.target.value, targetIds: [] })}>
                      <option value="all">Tous les produits</option>
                      <option value="products">Certains produits</option>
                    </select>

                    {p.targetType === 'products' && (
                      <select multiple size="6" className="vt-input vt-promo-products"
                        value={p.targetIds || []}
                        onChange={e => {
                          const selected = Array.from(e.target.selectedOptions).map(o => o.value);
                          updatePromotion(p.id, { targetIds: selected });
                        }}>
                        {allProducts.map(prod => (
                          <option key={prod._id} value={prod._id}>{prod.nom} ({prod.codeProduit})</option>
                        ))}
                      </select>
                    )}

                    {p.targetType === 'products' && (
                      <div className="vt-cfg-sub" style={{ marginTop: 4 }}>
                        Maintenez Cmd / Ctrl pour sélectionner plusieurs produits — {(p.targetIds || []).length} sélectionné(s).
                      </div>
                    )}

                    <div className="vt-promo-preview">
                      Réduction : <strong style={{ color: '#dc2626' }}>
                        −{p.type === 'percent' ? `${p.value || 0}%` : `${(p.value || 0).toLocaleString('fr-FR')} XOF`}
                      </strong>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Colonne preview — sticky */}
        <div className="vt-preview-col">
          <div className="vt-preview-sticky" style={{ '--accent': cfg.primaryColor }}>
            <div className="vt-preview-caption">
              <Eye size={11}/> Aperçu — design <strong>{cfg.design}</strong> · disposition <strong>{cfg.layout}</strong>
            </div>
            <div className="vt-preview-frame">
              <div className="vt-preview-bar">
                <span/><span/><span/>
                <span className="vt-preview-url">/shop/{boutique?._id?.slice(-6)}</span>
              </div>
              <div className="vt-preview-content">
                {(cfg.sections || []).filter(s => s.enabled).map((s, i) => {
                  if (s.type === 'hero') return (
                    <div key={i} className="vt-preview-hero-full"
                      style={{
                        backgroundImage: s.config?.imageUrl
                          ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.5)), url("${s.config.imageUrl}")`
                          : `linear-gradient(135deg, ${cfg.primaryColor}, color-mix(in srgb, ${cfg.primaryColor} 60%, #000 40%))`,
                        textAlign: s.config?.align || 'center'
                      }}>
                      {s.config?.tag && <span className="vt-preview-hero-tag">{s.config.tag}</span>}
                      <strong>{s.config?.title}</strong>
                      <span>{s.config?.subtitle}</span>
                      {s.config?.ctaLabel && (
                        <button style={{ background: cfg.primaryColor }}>{s.config.ctaLabel}</button>
                      )}
                    </div>
                  );
                  if (s.type === 'banner') return (
                    <div key={i} className="vt-preview-banner" style={{ background: s.config?.bgColor, color: s.config?.textColor }}>
                      <span>{s.config?.icon}</span> {s.config?.text}
                    </div>
                  );
                  if (s.type === 'featured') return (
                    <div key={i} className="vt-preview-block">
                      <div className="vt-preview-block-title">{s.config?.title || 'Produits phares'}</div>
                      <div className="vt-preview-block-grid">
                        {Array.from({ length: Math.max(3, Math.min(4, (s.config?.produitIds || []).length || 3)) }).map((_, k) => (
                          <div key={k} className="vt-mini-card vt-mini-card--featured">
                            <div className="vt-mini-img"/>
                            <div className="vt-mini-name">Produit phare</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                  if (s.type === 'categories') return (
                    <div key={i} className="vt-preview-block">
                      <div className="vt-preview-block-title">{s.config?.title || 'Catégories'}</div>
                      <div className="vt-preview-cats">
                        {(s.config?.items || []).map((it, k) => (
                          <span key={k} className="vt-preview-cat-chip">{it.label}</span>
                        ))}
                      </div>
                    </div>
                  );
                  if (s.type === 'testimonials') return (
                    <div key={i} className="vt-preview-block">
                      <div className="vt-preview-block-title">{s.config?.title || 'Témoignages'}</div>
                      <div className="vt-preview-testi">
                        {(s.config?.items || []).slice(0, 2).map((t, k) => (
                          <div key={k} className="vt-preview-testi-card">
                            <div>"{t.text || '…'}"</div>
                            <strong>— {t.author || 'Client'}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                  if (s.type === 'advantages') return (
                    <div key={i} className="vt-preview-block">
                      <div className="vt-preview-block-title">{s.config?.title}</div>
                      <div className="vt-preview-adv">
                        {(s.config?.items || []).map((it, k) => (
                          <div key={k} className="vt-preview-adv-card">
                            <div className="vt-preview-adv-icon">{it.icon}</div>
                            <strong>{it.title}</strong>
                            <span>{it.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                  if (s.type === 'stats') return (
                    <div key={i} className="vt-preview-stats" style={{ background: '#0f172a' }}>
                      {(s.config?.items || []).map((it, k) => (
                        <div key={k} className="vt-preview-stat">
                          <strong style={{ color: cfg.primaryColor }}>{it.value}</strong>
                          <span>{it.label}</span>
                        </div>
                      ))}
                    </div>
                  );
                  if (s.type === 'split') return (
                    <div key={i} className={`vt-preview-split vt-preview-split--${s.config?.imagePosition || 'right'}`}>
                      <div className="vt-preview-split-text">
                        <strong>{s.config?.title}</strong>
                        <span>{s.config?.subtitle}</span>
                        {s.config?.ctaLabel && <button style={{ background: cfg.primaryColor }}>{s.config.ctaLabel}</button>}
                      </div>
                      <div className="vt-preview-split-image"
                        style={{ backgroundImage: s.config?.imageUrl
                          ? `url("${s.config.imageUrl}")`
                          : `linear-gradient(135deg, ${cfg.primaryColor}, color-mix(in srgb, ${cfg.primaryColor} 50%, #000 50%))` }}/>
                    </div>
                  );
                  if (s.type === 'gallery') return (
                    <div key={i} className="vt-preview-block">
                      <div className="vt-preview-block-title">{s.config?.title}</div>
                      <div className="vt-preview-gallery">
                        {(s.config?.images || []).filter(Boolean).slice(0, 5).map((url, k) => (
                          <div key={k} className="vt-preview-gallery-item" style={{ backgroundImage: `url("${url}")` }}/>
                        ))}
                        {(s.config?.images || []).filter(Boolean).length === 0 &&
                          Array.from({ length: 3 }).map((_, k) => <div key={k} className="vt-preview-gallery-item"/>)}
                      </div>
                    </div>
                  );
                  if (s.type === 'faq') return (
                    <div key={i} className="vt-preview-block">
                      <div className="vt-preview-block-title">{s.config?.title}</div>
                      <div className="vt-preview-faq">
                        {(s.config?.items || []).slice(0, 2).map((it, k) => (
                          <div key={k} className="vt-preview-faq-item">
                            <strong>{it.question}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                  if (s.type === 'about') return (
                    <div key={i} className="vt-preview-about">
                      <strong>{s.config?.title}</strong>
                      <p>{s.config?.text}</p>
                    </div>
                  );
                  if (s.type === 'newsletter') return (
                    <div key={i} className="vt-preview-newsletter">
                      <strong>{s.config?.title}</strong>
                      <small>{s.config?.subtitle}</small>
                      <div className="vt-preview-newsletter-form">
                        <span>{s.config?.placeholder || 'email@…'}</span>
                        <button style={{ background: cfg.primaryColor }}>{s.config?.ctaLabel || 'S\'inscrire'}</button>
                      </div>
                    </div>
                  );
                  return null;
                })}
                {cfg.showProductGrid !== false && cfg.showSearch && <div className="vt-preview-search">🔍 Rechercher…</div>}
                {cfg.showProductGrid !== false && cfg.showProductGridTitle !== false && cfg.productGridTitle && (
                  <div className="vt-preview-grid-title">{cfg.productGridTitle}</div>
                )}
                {cfg.showProductGrid !== false && (
                <div className={`vt-preview-grid vt-preview-grid--${cfg.layout}`}>
                  {[1, 2, 3, 4].map(n => (
                    <div key={n} className={`vt-preview-card vt-preview-card--${cfg.design}`}>
                      <div className="vt-preview-img"/>
                      <div className="vt-preview-body">
                        <div className="vt-preview-name">Produit {n}</div>
                        <div className="vt-preview-foot">
                          <span className="vt-preview-price">{cfg.showPrice !== false ? '2 500 XOF' : 'Prix sur demande'}</span>
                          <button className="vt-preview-cta" style={{ background: cfg.primaryColor }}>
                            + {cfg.ctaLabel || 'Ajouter'}
                          </button>
                        </div>
                        {cfg.showStock && <div className="vt-preview-stock">Stock 42</div>}
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product picker modal */}
      {pickerOpen && (
        <>
          <div className="bt-overlay" onClick={() => setPickerOpen(false)}/>
          <div className="bt-modal" style={{ width: 640 }}>
            <div className="bt-modal-head">
              <div>
                <h2 className="bt-modal-title"><Sparkles size={14}/> Choisir les produits phares</h2>
                <p className="bt-modal-sub">{boutique?.nom} · {pickerSelected.length} sélectionné{pickerSelected.length !== 1 ? 's' : ''}</p>
              </div>
              <button className="bt-modal-close" onClick={() => setPickerOpen(false)}><X size={16}/></button>
            </div>
            <div className="bt-modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <div className="bt-picker-search">
                <Search size={13}/>
                <input type="text" placeholder="Rechercher un produit…"
                  value={pickerSearch} onChange={e => setPickerSearch(e.target.value)}/>
              </div>
              {pickerLoading ? (
                <div className="bt-orders-loading"><Loader size={16} className="bt-spin"/> Chargement…</div>
              ) : (
                <div className="bt-picker-list">
                  {pickerFiltered.map(p => {
                    const checked = pickerSelected.includes(p._id);
                    return (
                      <label key={p._id} className={`bt-picker-item ${checked ? 'bt-picker-item--checked' : ''}`}>
                        <input type="checkbox" checked={checked} onChange={() => togglePickerProduct(p._id)}/>
                        <div className="bt-picker-info">
                          <div className="bt-picker-name">{p.nom}</div>
                          <div className="bt-picker-meta">{p.codeProduit} · Stock {p.quantiteStock}</div>
                        </div>
                        <div className="bt-picker-price">
                          {new Intl.NumberFormat('fr-FR').format(p.prixVenteDetail || p.prixVente || 0)} XOF
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="bt-modal-footer">
              <button type="button" className="bt-btn-cancel" onClick={() => setPickerOpen(false)}>Annuler</button>
              <button type="button" className="bt-btn-confirm" onClick={confirmPicker}>
                <Check size={13}/> Valider ({pickerSelected.length})
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
